import React from "react";

export default function AnalyticsCharts({ stats, research, requests }) {
  const researchByYear = research.reduce((acc, doc) => {
    acc[doc.year] = (acc[doc.year] || 0) + 1;
    return acc;
  }, {});

  const researchByCampus = research.reduce((acc, doc) => {
    acc[doc.campus] = (acc[doc.campus] || 0) + 1;
    return acc;
  }, {});

  const requestsByStatus = requests.reduce((acc, req) => {
    acc[req.status] = (acc[req.status] || 0) + 1;
    return acc;
  }, {});

  const requestsByMonth = requests.reduce((acc, req) => {
    const month = new Date(req.dateRequested).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {});

  const maxResearchCount = Math.max(...Object.values(researchByYear), 1);
  
  // All SKSU campuses
  const allCampuses = ['Isulan', 'ACCESS', 'Tacurong', 'Bagumbayan', 'Kalamansig', 'Lutayan', 'Palimbang'];
  
  // Create research data for all campuses (including those with 0 research)
  const researchByCampusComplete = {};
  allCampuses.forEach(campus => {
    researchByCampusComplete[campus] = researchByCampus[campus] || 0;
  });
  
  const maxCampusCount = Math.max(...Object.values(researchByCampusComplete), 1);
  const maxStatusCount = Math.max(...Object.values(requestsByStatus), 1);

  // Color palette for different years
  const yearColors = [
    'bg-orange-500',
    'bg-blue-500', 
    'bg-green-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-red-500',
    'bg-yellow-500',
    'bg-cyan-500'
  ];

  // Color palette for different campuses
  const campusColors = {
    'Isulan': 'bg-blue-500',
    'ACCESS': 'bg-green-500',
    'Tacurong': 'bg-purple-500',
    'Bagumbayan': 'bg-orange-500',
    'Kalamansig': 'bg-pink-500',
    'Lutayan': 'bg-indigo-500',
    'Palimbang': 'bg-teal-500'
  };

  return (
    <div className="space-y-6">
      {/* Research by Year Chart */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold mb-4">Research Publications by Year</h3>
        <div className="space-y-2">
          {Object.entries(researchByYear)
            .sort(([a], [b]) => a - b)
            .map(([year, count], index) => (
              <div key={year} className="flex items-center gap-4">
                <span className="w-16 text-sm">{year}</span>
                <div className="flex-1 bg-gray-700 rounded-full h-6">
                  <div
                    className={`${yearColors[index % yearColors.length]} h-6 rounded-full transition-all duration-300`}
                    style={{ width: `${(count / maxResearchCount) * 100}%` }}
                  ></div>
                </div>
                <span className="w-12 text-sm text-right">{count}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Research by Campus Chart */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold mb-4">Research by Campus</h3>
        <div className="grid grid-cols-7 gap-4 items-end h-48">
          {allCampuses.map((campus, index) => (
            <div key={campus} className="flex flex-col items-center">
              <div className="text-center mb-2">
                <div className="text-lg font-semibold text-white">
                  {researchByCampusComplete[campus]}
                </div>
              </div>
              <div 
                className={`w-full rounded-t transition-all duration-300 ${campusColors[campus]}`}
                style={{ 
                  height: `${Math.max((researchByCampusComplete[campus] / maxCampusCount) * 120, 8)}px` 
                }}
              ></div>
              <div className="text-xs text-gray-300 mt-2 text-center leading-tight">
                {campus}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Request Status Chart */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold mb-4">Access Request Status</h3>
        <div className="space-y-2">
          {Object.entries(requestsByStatus).map(([status, count]) => (
            <div key={status} className="flex items-center gap-4">
              <span className="w-24 text-sm capitalize">{status}</span>
              <div className="flex-1 bg-gray-700 rounded-full h-6">
                <div
                  className={`h-6 rounded-full transition-all duration-300 ${
                    status === 'approved' ? 'bg-emerald-500' :
                    status === 'declined' ? 'bg-rose-500' : 'bg-amber-500'
                  }`}
                  style={{ width: `${(count / maxStatusCount) * 100}%` }}
                ></div>
              </div>
              <span className="w-12 text-sm text-right">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Request Trends */}
      <div className="bg-gray-800 p-6 rounded-lg">
        <h3 className="text-xl font-semibold mb-4">Monthly Request Trends</h3>
        <div className="space-y-2">
          {Object.entries(requestsByMonth)
            .sort(([a], [b]) => new Date(a) - new Date(b))
            .slice(-6) // Show last 6 months
            .map(([month, count]) => (
              <div key={month} className="flex items-center gap-4">
                <span className="w-20 text-sm">{month}</span>
                <div className="flex-1 bg-gray-700 rounded-full h-6">
                  <div
                    className="bg-pink-500 h-6 rounded-full transition-all duration-300"
                    style={{ width: `${(count / Math.max(...Object.values(requestsByMonth), 1)) * 100}%` }}
                  ></div>
                </div>
                <span className="w-12 text-sm text-right">{count}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-orange-800 p-4 rounded-lg text-center">
          <h4 className="text-lg font-semibold">Total Research</h4>
          <p className="text-3xl font-bold">{stats.summary?.totalResearch || 0}</p>
        </div>
        <div className="bg-cyan-800 p-4 rounded-lg text-center">
          <h4 className="text-lg font-semibold">Total Users</h4>
          <p className="text-3xl font-bold">{stats.summary?.totalUsers || 0}</p>
        </div>
        <div className="bg-pink-800 p-4 rounded-lg text-center">
          <h4 className="text-lg font-semibold">Total Requests</h4>
          <p className="text-3xl font-bold">{stats.summary?.totalRequests || 0}</p>
        </div>
        <div className="bg-emerald-800 p-4 rounded-lg text-center">
          <h4 className="text-lg font-semibold">Approval Rate</h4>
          <p className="text-3xl font-bold">
            {stats.summary?.totalRequests > 0 
              ? Math.round((requestsByStatus.approved || 0) / stats.summary.totalRequests * 100)
              : 0}%
          </p>
        </div>
      </div>
    </div>
  );
} 